import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import AppHeader from "./AppHeader";
import { 
  FaUsers, FaStore, FaUserTie, FaGlobe, 
  FaUserCheck, FaSignInAlt, FaSignOutAlt, FaLaptopHouse, FaCommentDots, FaStar, FaEnvelope, FaMapMarkerAlt, FaPhoneAlt, FaGlobeAmericas
} from 'react-icons/fa';
import { MdClose } from "react-icons/md";

// ---------- Styled Components ----------
const Container = styled.div`
  background-color: #f5f7fa;
  min-height: 100vh;
  padding-bottom: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 2rem;
  color: #1a202c;
  margin: 30px 0;
  font-weight: 700;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  width: 90%;
  max-width: 1200px;
  margin-bottom: 40px;
`;

const StatCard = styled.div`
  background: white;
  padding: 25px;
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 20px;
  transition: transform 0.2s;
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};

  &:hover {
    transform: ${props => props.$isClickable ? 'translateY(-5px)' : 'none'};
  }
`;

const IconWrapper = styled.div`
  background: ${props => props.$bgColor || '#e2e8f0'};
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
`;

const StatLabel = styled.span`
  font-size: 0.9rem;
  color: #718096;
  font-weight: 500;
`;

// ---------- Business Popup Styles ----------
const ModalOverlay = styled.div`
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0,0,0,0.8); display: flex; justify-content: center; align-items: center; z-index: 5000;
`;

const ModalContent = styled.div`
    background: #f1f3f6; border-radius: 20px; width: 95%; max-width: 1000px;
    max-height: 85vh; overflow-y: auto; position: relative; padding: 30px;
    box-shadow: 0 20px 40px rgba(0,0,0,0.4);
`;

const BusinessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
  margin-top: 20px;
`;

const BizCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  position: relative;
`;

const CoverImg = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
  background: #cbd5e0;
`;

const LogoWrapper = styled.div`
  width: 80px;
  height: 80px;
  margin-top: -40px;
  margin-left: 20px;
  border-radius: 12px;
  border: 4px solid white;
  background: white;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
`;

const LogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BizDetails = styled.div`
  padding: 15px 20px 20px;
  
  h4 { margin: 0 0 5px 0; color: #1a202c; font-size: 1.2rem; }
  .tagline { color: #718096; font-size: 0.85rem; font-style: italic; margin-bottom: 12px; }
  
  .info-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
    font-size: 0.9rem;
    color: #4a5568;
    word-break: break-all;
  }
`;

const CloseBtn = styled.button`
  position: absolute; top: 15px; right: 15px; background: #fff; border: none;
  border-radius: 50%; width: 40px; height: 40px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; font-size: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1); z-index: 10;
  &:hover { background: #f8f9fa; }
`;

// ---------- Rest of Code ----------
const SectionContainer = styled.div`
  width: 90%; max-width: 1200px; background: white; padding: 25px;
  border-radius: 15px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const TableWrapper = styled.div` overflow-x: auto; margin-top: 20px; `;

const FeedbackTable = styled.table`
  width: 100%; border-collapse: collapse;
  th, td { padding: 12px 15px; border-bottom: 1px solid #edf2f7; font-size: 0.95rem; }
  th { background-color: #f8fafc; color: #4a5568; font-weight: 600; text-align: left; }
`;

const LoginModal = styled.div`
    background: white; padding: 40px; border-radius: 15px; width: 90%; max-width: 400px;
`;

const AdminInput = styled.input`
    width: 100%; padding: 14px; margin: 10px 0; border: 1px solid #e2e8f0;
    border-radius: 10px; outline: none; box-sizing: border-box;
`;

const AdminBtn = styled.button`
    width: 100%; background: #005b63; color: white; border: none;
    padding: 14px; border-radius: 10px; cursor: pointer; font-weight: 600; font-size: 1rem;
`;

const Dashboard = () => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [showBizModal, setShowBizModal] = useState(false);
  
  const [stats, setStats] = useState({
    totalUsers: 0, loggedIn: 0, loggedOut: 0, activeUsers: 0,
    businessOwners: 0, employees: 0, publicUsers: 0, businessPages: 0
  });
  const [feedbacks, setFeedbacks] = useState([]);
  const [businessPages, setBusinessPages] = useState([]);

  const handleAdminLogin = () => {
    if (adminEmail === "support@avaami.com" && adminPassword === "Admin@123$") {
      setIsAdminAuthenticated(true);
    } else {
      alert("Invalid Admin Credentials");
    }
  };

  useEffect(() => {
    if (!isAdminAuthenticated) return;

    const fetchAllData = async () => {
      try {
        const usersSnap = await getDocs(query(collection(db, "users")));
        let total = 0, inCount = 0, outCount = 0, activeU = 0;
        usersSnap.forEach((doc) => {
          total++;
          const d = doc.data();
          if (d.status === "logged_in") inCount++; else if (d.status === "logged_out") outCount++;
          if (d.status === "active") activeU++;
        });

        const profilesSnap = await getDocs(query(collection(db, "profiles")));
        let biz = 0, emp = 0, pub = 0;
        profilesSnap.forEach((doc) => {
          const role = doc.data().mainRole?.toLowerCase() || "";
          if (role.includes("business")) biz++; else if (role.includes("employee")) emp++; else pub++;
        });

        // Fetching Business Pages with your structure
        const bizSnap = await getDocs(query(collection(db, "businessPages")));
        const bizList = bizSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const fbSnap = await getDocs(query(collection(db, "feedbacks"), orderBy("createdAt", "desc")));
        const fbList = fbSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        setBusinessPages(bizList);
        setFeedbacks(fbList);
        setStats({
          totalUsers: total, loggedIn: inCount, loggedOut: outCount, activeUsers: activeU,
          businessOwners: biz, employees: emp, publicUsers: pub, businessPages: bizSnap.size
        });
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchAllData();
  }, [isAdminAuthenticated]);

  const cardData = [
    { label: "Total Users", value: stats.totalUsers, icon: <FaUsers />, color: "#4e73df" },
    { label: "Users Logged In", value: stats.loggedIn, icon: <FaSignInAlt />, color: "#1cc88a" },
    { label: "Users Logged Out", value: stats.loggedOut, icon: <FaSignOutAlt />, color: "#e74a3b" },
    { label: "Active Users", value: stats.activeUsers, icon: <FaUserCheck />, color: "#36b9cc" },
    { label: "Business Owners", value: stats.businessOwners, icon: <FaStore />, color: "#f6c23e" },
    { label: "Employees", value: stats.employees, icon: <FaUserTie />, color: "#858796" },
    { label: "Public Users", value: stats.publicUsers, icon: <FaGlobe />, color: "#5a5c69" },
    { label: "Business Pages", value: stats.businessPages, icon: <FaLaptopHouse />, color: "#6f42c1", clickable: true },
  ];

  if (!isAdminAuthenticated) {
    return (
      <>
        <AppHeader />
        <ModalOverlay>
          <LoginModal>
            <h2 style={{ textAlign: "center", color: "#005b63", marginBottom: '20px' }}>Admin Portal</h2>
            <AdminInput placeholder="Admin Email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} />
            <AdminInput type="password" placeholder="Password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} />
            <AdminBtn onClick={handleAdminLogin}>Unlock Dashboard</AdminBtn>
          </LoginModal>
        </ModalOverlay>
      </>
    );
  }

  return (
    <>
      <AppHeader />
      <Container>
        <Title>Admin Dashboard</Title>
        {loading ? <div style={{marginTop: '50px'}}>Loading Statistics...</div> : (
          <>
            <StatsGrid>
              {cardData.map((item, i) => (
                <StatCard key={i} $isClickable={item.clickable} onClick={item.clickable ? () => setShowBizModal(true) : null}>
                  <IconWrapper $bgColor={item.color}>{item.icon}</IconWrapper>
                  <StatInfo>
                    <StatValue>{item.value}</StatValue>
                    <StatLabel>{item.label}</StatLabel>
                  </StatInfo>
                </StatCard>
              ))}
            </StatsGrid>

            {/* Business Pages Popup - Based on your DB Screenshot */}
            {showBizModal && (
              <ModalOverlay onClick={() => setShowBizModal(false)}>
                <ModalContent onClick={e => e.stopPropagation()}>
                  <CloseBtn onClick={() => setShowBizModal(false)}><MdClose /></CloseBtn>
                  <h2 style={{ color: "#1a202c", marginBottom: "5px" }}>Registered Business Pages</h2>
                  <p style={{ color: "#718096", marginBottom: "25px" }}>Viewing {businessPages.length} active business profiles</p>
                  
                  <BusinessGrid>
                    {businessPages.map((biz) => (
                      <BizCard key={biz.id}>
                        {/* Using coverImageUrl from your screenshot */}
                        <CoverImg src={biz.coverImageUrl || "https://via.placeholder.com/400x140?text=No+Cover+Image"} alt="Cover" />
                        <LogoWrapper>
                          {/* Using logoUrl from your screenshot */}
                          <LogoImg src={biz.logoUrl || "https://via.placeholder.com/80?text=Logo"} alt="Logo" />
                        </LogoWrapper>
                        <BizDetails>
                          <h4>{biz.businessName || "Unnamed Business"}</h4>
                          {biz.tagline && <p className="tagline">"{biz.tagline}"</p>}
                          
                          {/* Using email field from your screenshot */}
                          <div className="info-row"><FaEnvelope color="#005b63" /> {biz.email || "No Email"}</div>
                          {/* Using location field from your screenshot */}
                          <div className="info-row"><FaMapMarkerAlt color="#e74a3b" /> {biz.location || "No Location"}</div>
                          {/* Using phone field from your screenshot */}
                          <div className="info-row"><FaPhoneAlt color="#1cc88a" /> {biz.phone || "No Phone"}</div>
                          {/* Using website field from your screenshot */}
                          {biz.website && (
                            <div className="info-row"><FaGlobeAmericas color="#4e73df" /> {biz.website}</div>
                          )}
                        </BizDetails>
                      </BizCard>
                    ))}
                  </BusinessGrid>
                </ModalContent>
              </ModalOverlay>
            )}

            <SectionContainer>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#2d3748' }}>
                <FaCommentDots color="#1a73e8" /> User Feedbacks
              </h3>
              <TableWrapper>
                <FeedbackTable>
                  <thead>
                    <tr><th>User</th><th>Email</th><th>Rating</th><th>Message</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {feedbacks.map(fb => (
                      <tr key={fb.id}>
                        <td><b>{fb.userName}</b></td>
                        <td>{fb.userEmail}</td>
                        <td style={{color: '#ecc94b'}}>{[...Array(Number(fb.rating) || 0)].map((_, i) => <FaStar key={i} />)}</td>
                        <td>{fb.message}</td>
                        <td>{fb.createdAt?.toDate().toLocaleDateString('en-GB')}</td>
                      </tr>
                    ))}
                  </tbody>
                </FeedbackTable>
              </TableWrapper>
            </SectionContainer>
          </>
        )}
      </Container>
    </>
  );
};

export default Dashboard;